import { Button, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

const CustomAgTextFilter = (props: any) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [value, setValue] = useState(''); // current filter text
  const [isActive, setIsActive] = useState(false); // filter active state
  const inputRef = useRef<HTMLInputElement>(null);

  // Update AG Grid filter when typing in floating filter
  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setValue(newValue);
      // reflect active state immediately while typing
      setIsActive(!!newValue);

      props.parentFilterInstance?.((instance: any) => {
        // Use "contains" as default; you can adjust
        instance.onFloatingFilterChanged('contains', newValue);
      });
    },
    [props],
  );

  useEffect(() => {
    if (!props.model) setValue('');
  }, [props.model]);

  // Detect filter active state and sync popup value
  useEffect(() => {
    const updateFromModel = () => {
      // If user is actively typing, avoid resetting value/focus
      if (document.activeElement === inputRef.current) return;
      const filterModel = props.api.getFilterModel?.();
      const colId = props.column.getColId?.();
      const model = filterModel?.[colId];

      // Detect active for text filter model variants
      const modelHasValue = !!(
        (typeof model?.filter === 'string' && model.filter.length > 0) ||
        (Array.isArray(model?.conditions) && model.conditions.some((c: any) => !!c?.filter))
      );

      // Prefer first condition's value for floating input when present
      const firstConditionValue =
        Array.isArray(model?.conditions) && model.conditions.length > 0
          ? (model.conditions[0]?.filter ?? '')
          : (model?.filter ?? '');

      if (modelHasValue) {
        setValue(firstConditionValue);
        setIsActive(true);
      } else {
        setValue('');
        setIsActive(false);
      }
    };

    // Initial check
    updateFromModel();

    // Listen to AG Grid filter changes
    props.api?.addEventListener('filterChanged', updateFromModel);
    return () => {
      props.api?.removeEventListener('filterChanged', updateFromModel);
    };
  }, [props.api, props.column]);

  // Open AG Grid filter popup below the input
  const onFilterIconClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    props.showParentFilter?.(inputRef.current);
  };

  return (
    <Box
      sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <Stack
        direction="row"
        sx={{
          border: `1px solid ${theme.palette.borderLight}`,
          borderRadius: `${theme.borderRadius.sm}px`,
          backgroundColor: theme.palette.common.white,
          width: '100%',
          height: '25px',
          gap: '2px',
          overflow: 'hidden',
        }}
        alignItems={'center'}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <TextField
          value={value}
          onChange={onInputChange}
          placeholder={t('common.search')}
          inputRef={inputRef}
          onKeyDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{
            flex: 1,
            minWidth: 0,
            '& .MuiOutlinedInput-root': {
              padding: 0,
              boxShadow: 'none',
              paddingLeft: '5px',
              fontSize: '12px',
              '& fieldset': { border: 'none' },
              '&:hover fieldset': { border: 'none' },
              '&.Mui-focused fieldset': { border: 'none' },
            },
            '& .MuiOutlinedInput-root.Mui-focused': {
              boxShadow: 'none',
            },
            '& .MuiOutlinedInput-input': {
              padding: 0,
              height: '20px',
            },
          }}
        />

        {/* Filter icon */}
        <Button
          type="button"
          className="ag-icon ag-icon-filter"
          sx={{
            cursor: 'pointer',
            fontSize: '14px',
            height: '100%',
            width: '20px',
            minWidth: '30px',
            padding: 0,
            borderRadius: '0px',
            backgroundColor: isActive ? theme.palette.leftNavigation.activeBg : 'transparent',
            color: isActive ? theme.palette.primary.main : theme.palette.textMedium,
            transition: `all ${theme.transitions.custom.fast}`,
            '&:hover': {
              backgroundColor: isActive
                ? theme.palette.leftNavigation.activeBg
                : 'rgba(0,0,0,0.05)',
            },
          }}
          onClick={onFilterIconClick}
        />
      </Stack>
    </Box>
  );
};

export default CustomAgTextFilter;
